import { Card } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Target, CheckCircle2, TrendingUp } from 'lucide-react';
import type { SprintProgress as SprintProgressData } from '@/types';

interface SprintProgressProps {
  sprint: SprintProgressData;
}

export function SprintProgress({ sprint }: SprintProgressProps) {
  const progressPercentage = Math.round((sprint.completed / sprint.total) * 100);
  const inProgress = sprint.inProgress ?? 0;
  const pending = sprint.pending ?? Math.max(0, sprint.total - sprint.completed - inProgress);

  return (
    <Card 
      title="Sprint Progress" 
      subtitle="Track your project milestone progress"
    >
      <div className="space-y-4">
        {/* Sprint header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-slate-900">{sprint.name}</h4>
            <p className="text-sm text-slate-500">{sprint.description}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Overall Progress</span>
            <span className="text-lg font-bold text-slate-900">{progressPercentage}%</span>
          </div>
          <ProgressBar value={progressPercentage} color="bg-blue-500" size="lg" />
          <p className="text-sm text-slate-500 mt-1">
            {sprint.completed} of {sprint.total} tasks completed
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-green-50 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-600">Done</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{sprint.completed}</p>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600">Active</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{inProgress}</p>
          </div>

          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Target className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-600">Pending</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{pending}</p>
          </div>
        </div>

        {/* Milestone indicator */}
        <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-slate-700">
              Sprint {sprint.completed >= sprint.total ? 'Complete!' : `in Progress (${Math.round((sprint.total - sprint.completed) / sprint.total * 100)}% remaining)`}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
